//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { PoolTypeEnum, PoolTypeToString, yupErc20Wei, yupEthAddr } from './Enums'
import { CTOReducerEnum, EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
export const PoolBaseAccountLenderCTOSchemaValues: Record<string, yup.Schema> = {
  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  poolAddr: yupEthAddr.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  accountAddr: yupEthAddr.label('Account Address').meta(
    toFieldMetaData({
      propName: 'accountAddr',
      description: 'The white-listed blockchain wallet address associated with this account. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  tokenBalance: yupErc20Wei.label('Vault Token Balance').meta(
    toFieldMetaData({
      propName: 'tokenBalance',
      description: 'The number of vault tokens held by the account.',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  interestRate: yupErc20Wei.label('Loan Fee Rate').meta(
    toFieldMetaData({
      propName: 'interestRate',
      description:
        'The current annualised interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
      stringType: StringTypeEnum.APR,
    })
  ),
  earlyWithdrawInterestAdjustment: yupErc20Wei.label('Early Withdraw Interest Adjustment').meta(
    toFieldMetaData({
      propName: 'earlyWithdrawInterestAdjustment',
      description: 'Interest earned adjustment due to early withdraw ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',

      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}
export const PoolBaseAccountCTOSchemaValues: Record<string, yup.Schema> = {
  id: yup
    .string()
    .label('PoolId poolAddr-lenderAddr')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of poolAccount ', stringType: StringTypeEnum.ID })),
  blockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block Number' })),
  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  dayNumber: yup
    .number()
    .label('Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'day number of timestamp',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  timeOfDay: yup
    .number()
    .label('Time of Day')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timeOfDay',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_TIME_OF_DAY,
      })
    ),

  poolType: yup
    .number()
    .label('Pool Type')
    .required()
    .meta(toFieldMetaData({ propName: 'poolType', description: 'Type of pool ', stateToString: PoolTypeToString })),

  poolAddr: yupEthAddr.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  liquidityAssetAddr: yupEthAddr.label('Liquidity Asset Address').meta(
    toFieldMetaData({
      propName: 'liquidityAssetAddr',
      description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  accountAddr: yupEthAddr.label('Account Address').meta(
    toFieldMetaData({
      propName: 'accountAddr',
      description: 'The white-listed blockchain wallet address associated with this account. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  tokenBalance: yupErc20Wei.label('Vault Token Balance').meta(
    toFieldMetaData({
      propName: 'tokenBalance',
      description: 'The number of vault tokens held by the account.',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  interestRate: yupErc20Wei.label('Loan Fee Rate').meta(
    toFieldMetaData({
      propName: 'interestRate',
      description:
        'The current annualised interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
      stringType: StringTypeEnum.APR,
    })
  ),

  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',

      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const PoolBaseAccountSummaryCTOSchemaValues: Record<string, yup.Schema> = {
  startBlockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startBlockNumber',
        description: 'Block Number',
        map: 'blockNumber',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  startTimestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startTimestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
        map: 'timestamp',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  startDayNumber: yup
    .number()
    .label('Start Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startDayNumber',
        description: 'startDayNumber',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
        map: 'dayNumber',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  endDayNumber: yup
    .number()
    .label('End Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endDayNumber',
        description: 'endDayNumber',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
        map: 'dayNumber',
        reducer: CTOReducerEnum.LAST,
      })
    ),
  endBlockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endBlockNumber',
        description: 'Block Number',
        map: 'blockNumber',
        reducer: CTOReducerEnum.LAST,
      })
    ),
  endTimestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endTimestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
        map: 'timestamp',
        reducer: CTOReducerEnum.LAST,
      })
    ),

  averageTokenBalance: yupErc20Wei.label('AverageVault Token Balance').meta(
    toFieldMetaData({
      propName: 'averageTokenBalance',
      description: 'The number of vault tokens held by the account.',
      stringType: StringTypeEnum.PPT_SHARES,
      map: 'tokenBalance',
      reducer: CTOReducerEnum.AVERAGE,
    })
  ),

  sumInterestInADay: yupErc20Wei.label('Sum of daily interest').meta(
    toFieldMetaData({
      propName: 'sumInterestInADay',
      description: 'Sum of daily interest.',
      stringType: StringTypeEnum.STABLE_COINS,
      map: 'interestInDay',
      reducer: CTOReducerEnum.SUM,
    })
  ),
  averagePrincipalEarningInterest: yupErc20Wei.label('Average Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'averagePrincipalEarningInterest',
      description: 'Average Principal Earning Interest',
      stringType: StringTypeEnum.STABLE_COINS,
      map: 'principalEarningInterest',
      reducer: CTOReducerEnum.SUM,
    })
  ),
}

export const PoolBaseAccountCTOSchema = yup.object(PoolBaseAccountCTOSchemaValues)
export const PoolBaseAccountLenderFlexCTOSchemaValues: Record<string, yup.Schema> = {
  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  poolAddr: yupEthAddr.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  accountAddr: yupEthAddr.label('Account Address').meta(
    toFieldMetaData({
      propName: 'accountAddr',
      description: 'The white-listed blockchain wallet address associated with this account. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  tokenBalance: yupErc20Wei.label('Vault Token Balance').meta(
    toFieldMetaData({
      propName: 'tokenBalance',
      description: 'The number of vault tokens held by the account.',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  interestRate: yupErc20Wei.label('Loan Fee Rate').meta(
    toFieldMetaData({
      propName: 'interestRate',
      description:
        'The current annualised interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
      stringType: StringTypeEnum.APR,
    })
  ),

  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',

      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}
export interface PoolBaseAccountCTO extends InferType<typeof PoolBaseAccountCTOSchema> {
  poolType: PoolTypeEnum
  poolAddr: EthAddr
  accountAddr: EthAddr
  tokenBalance: ERC20Wei
  liquidityAssetAddr: EthAddr
  principalEarningInterest: ERC20Wei
  timestamp: number
  timeOfDay: number
  dayNumber: number
  blockNumber: number
}
